


































































import Vue from 'vue';
import i18n from 'vue-i18n';
import { v4 as uuidv4 } from 'uuid';
import { isEqual } from 'lodash';

import CoButton from '../../Atoms/co-button/CoButton.vue';
import CoInput from '../co-input/CoInput.vue';
import CoFormGroup from '../co-form-group/CoFormGroup.vue';
import CoHumanVerification from '../co-human-verification/CoHumanVerification.vue';

Vue.use(i18n);

interface Input {
    Label?: string;
    Type?: string;
    Name: string;
    Value: unknown;
    Required?: boolean;
    Placeholder?: string;
    Disabled?: boolean;
    validationHint?: string;
    validationRegExp?: RegExp;
    suggester?: (...args: unknown[]) => unknown;
    Autocomplete?: string;
    ValidateOnMount?: boolean;
}

export default {
    name: 'CoForm',
    components: {
        CoButton,
        CoInput,
        CoFormGroup,
        CoHumanVerification,
    },
    props: {
        inputs: {
            type: Array as () => Input[],
            default: () => [],
        },
        hideReset: {
            type: Boolean,
            required: false,
            default: false,
        },
        hideSubmit: {
            type: Boolean,
            required: false,
            default: false,
        },
        submitLabel: {
            type: String,
            required: false,
            default: null,
        },
        submitBtnVariant: {
            type: String,
            required: false,
            default: 'primary',
        },
        resetLabel: {
            type: String,
            required: false,
            default: null,
        },
        resetBtnVariant: {
            type: String,
            required: false,
            default: 'default',
        },
        loading: {
            type: Boolean,
            required: false,
            default: false,
        },
        disabled: {
            type: Boolean,
            required: false,
            default: false,
        },
        submitMethod: {
            type: Function,
            required: false,
            default: null,
        },
        resetMethod: {
            type: Function,
            required: false,
            default: null,
        },
        humanVerification: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    data() {
        return {
            formId: `co-form-${uuidv4()}`,
            form: null,
            formData: {},
            humanVerificationPayload: '',
            humanVerificationState: 'unverified',
            isValid: null,
            originalData: {},
            hasChanges: false,
            inputComponents: [],
            loadingLocal: this.loading,
        };
    },
    mounted() {
        this.initializeFromInputs();
    },
    watch: {
        inputs: {
            handler() {
                this.initializeFromInputs();
            },
            deep: true,
        },
        formData: {
            handler(newVal) {
                // check if the form data has changed
                this.hasChanges = !isEqual(newVal, this.originalData);
                this.checkValidity();
            },
            deep: true,
        },
        loading(newVal) {
            this.loadingLocal = newVal;
        },
    },
    computed: {
        canSubmit() {
            return (
                !this.disabled &&
                !this.loadingLocal &&
                this.isValid &&
                this.hasChanges &&
                this.isHumanVerificationValid()
            );
        },
    },
    methods: {
        initializeFromInputs() {
            const nextData = {};
            this.inputs.forEach((input) => {
                nextData[input.Name] = input.Value ? JSON.parse(JSON.stringify(input.Value)) : input.Value;
            });

            Object.keys(this.formData).forEach((key) => {
                if (!(key in nextData)) {
                    this.$delete(this.formData, key);
                }
            });
            Object.entries(nextData).forEach(([key, value]) => {
                this.$set(this.formData, key, value);
            });

            this.originalData = JSON.parse(JSON.stringify(this.formData));
            this.hasChanges = false;

            this.$nextTick(() => {
                this.inputs.forEach((input) => {
                    const ref = this.$refs[input.Name];
                    const value = this.formData[input.Name];
                    if (ref && ref[0] && value !== undefined && value !== null && value !== '') {
                        ref[0].inputValidation(value);
                    }
                });
                this.checkValidity();
            });
        },
        checkValidity() {
            // check the form validity
            const hasInvalidInput = Object.keys(this.formData).some(
                (key) => this.$refs[key] && this.$refs[key][0].isValid === false
            );
            if (hasInvalidInput) {
                this.isValid = false;
                return;
            }
            // if no invalid inputs are found, set the form to valid
            this.isValid = true;
        },
        getSubmissionData() {
            const submissionData = { ...this.formData };
            if (this.humanVerification) {
                submissionData.captcha_payload = this.humanVerificationPayload;
            }
            return submissionData;
        },
        handleHumanVerificationStatus(humanVerificationStatus) {
            this.humanVerificationState = humanVerificationStatus.state;
            this.humanVerificationPayload = humanVerificationStatus.payload || '';
            this.checkValidity();
        },
        isHumanVerificationValid() {
            return !this.humanVerification || this.humanVerificationState === 'verified';
        },
        submit() {
            // handler to programmatically submit the form from outside the component
            this.handleSubmit();
        },
        async handleSubmit() {
            this.checkValidity();
            // Apply the same guard for Enter-key and programmatic submissions as for the submit button.
            if (!this.canSubmit) {
                return;
            }

            this.loadingLocal = true;
            const submissionData = this.getSubmissionData();
            try {
                if (this.submitMethod) {
                    // if a submit method is provided, call it
                    // supports promises and async functions
                    await this.submitMethod(submissionData);
                }
                // emit the form data on submit
                this.$emit('submit', submissionData);
            } finally {
                this.loadingLocal = false;
            }
        },
        async resetForm() {
            if (this.resetMethod) {
                // if a reset method is provided, call it
                // supports promises and async functions
                await this.resetMethod(this.formData);
            } else {
                // default: reset the form to its original state
                Object.entries(this.originalData).forEach(([key, value]) => {
                    this.formData[key] = value;
                });
            }
            this.resetHumanVerification();
            this.$emit('reset', this.formData);
        },
        resetHumanVerification() {
            this.humanVerificationPayload = '';
            this.humanVerificationState = 'unverified';
            const { humanVerification } = this.$refs;
            if (humanVerification && humanVerification.reset) {
                humanVerification.reset();
            }
        },
    },
};
